import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Phone, Mail, MapPin, MessageCircle, Clock, Building2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';
import { useSiteContent } from '../context/SiteContentContext';
import SEO from '../components/SEO';

const PAGE_BANNER = 'https://jilongot.com/public/upload/20240703/240F3163430613.jpg';

const Breadcrumb = ({ items }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-2 text-sm text-gray-600" data-testid="kontak-breadcrumb">
    {items.map((it, i) => (
      <React.Fragment key={i}>
        {i > 0 && <ChevronRight size={14} className="text-gray-400" />}
        {it.to ? (
          <Link to={it.to} className="hover:text-[#0a3d8f] transition-colors">{it.label}</Link>
        ) : (
          <span className="text-[#0a3d8f] font-semibold">{it.label}</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const InfoCard = ({ Icon, label, value, href, testId }) => {
  const inner = (
    <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all h-full">
      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#0a3d8f] to-[#0a8f3d] text-white flex items-center justify-center mb-4 shadow-md">
        <Icon size={22} />
      </div>
      <p className="text-xs uppercase tracking-wider text-[#0a3d8f] font-semibold mb-1.5">{label}</p>
      <p className="text-gray-800 text-sm leading-relaxed break-words">{value}</p>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" data-testid={testId} className="block">
      {inner}
    </a>
  ) : (
    <div data-testid={testId}>{inner}</div>
  );
};

const KontakPage = () => {
  const { config } = useSiteContent();
  const phone = config.phone || '';
  const whatsapp = config.whatsapp || '';
  const email = config.email || '';
  const address = config.address || '';
  const waNumber = whatsapp.replace(/[^0-9]/g, '');
  const mapsQuery = encodeURIComponent(address || 'Jakarta, Indonesia');
  const mapsEmbed = `https://www.google.com/maps?q=${mapsQuery}&output=embed`;

  // Editable Service Center card fields (with sensible fallbacks)
  const scTitle = config.service_center_title || 'Hubungi Kami';
  const scSubtitle = config.service_center_subtitle ?? '';
  const scHoursLabel = config.service_center_hours_label || 'Jam Operasional';
  const scHoursWeekday = config.service_center_hours_weekday || 'Senin – Jumat, 09.00 – 17.00 WIB';
  const scHoursWeekend = config.service_center_hours_weekend || 'Sabtu 09.00 – 13.00 WIB · Minggu Tutup';
  const scAddress = config.service_center_address || address;
  const scWaLabel = config.service_center_wa_button_label || 'Chat WhatsApp Sekarang';
  const scWaMessage = config.service_center_wa_message || 'Halo JILONG Indonesia, saya ingin konsultasi produk.';
  const scPhoneLabel = config.service_center_phone_button_label || 'Telepon Service Center';

  return (
    <>
      <SEO path="/kontak" pageName="Kontak" />
      <Header />
      <main data-testid="kontak-page-root">
        {/* Hero */}
        <section className="relative h-[280px] md:h-[340px] overflow-hidden">
          <img src={PAGE_BANNER} alt="Contact" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a3d8f]/85 via-[#0a3d8f]/60 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
            <p className="text-white/80 text-sm uppercase tracking-[0.3em] mb-3">Contact</p>
            <h1 className="text-white text-3xl md:text-5xl font-extrabold mb-3 font-[Montserrat]">Hubungi Kami</h1>
            <p className="text-white/90 text-base md:text-lg max-w-2xl leading-relaxed">
              Tim JILONG Indonesia siap membantu konsultasi produk, penawaran harga, dan layanan purna jual di seluruh Indonesia.
            </p>
          </div>
        </section>

        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Contact' }]} />

        {/* Quick contact info cards */}
        <section className="pb-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <InfoCard Icon={MapPin} label="Alamat" value={address} testId="kontak-info-address" />
              <InfoCard Icon={Phone} label="Telepon" value={phone} href={phone ? `tel:${phone.replace(/\s+/g, '')}` : undefined} testId="kontak-info-phone" />
              <InfoCard
                Icon={MessageCircle}
                label="WhatsApp"
                value={whatsapp}
                href={waNumber ? `https://wa.me/${waNumber}` : undefined}
                testId="kontak-info-whatsapp"
              />
              <InfoCard Icon={Mail} label="Email" value={email} href={email ? `mailto:${email}` : undefined} testId="kontak-info-email" />
            </div>
          </div>
        </section>

        {/* Map + Office */}
        <section className="pb-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-xl border border-gray-100 min-h-[380px] bg-gray-100">
                <iframe
                  title="JILONG Indonesia Location"
                  src={mapsEmbed}
                  width="100%"
                  height="100%"
                  className="w-full h-full min-h-[380px]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="kontak-map"
                />
              </div>
              <div className="bg-gradient-to-br from-[#0a3d8f] to-[#082e6b] rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/10 rounded-full" />
                <div className="absolute -bottom-16 -left-8 w-48 h-48 bg-[#0a8f3d]/20 rounded-full" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-lg bg-white/15 flex items-center justify-center mb-4">
                    <Building2 size={22} />
                  </div>
                  <h3 className="text-2xl font-extrabold mb-1 font-[Montserrat]">{scTitle}</h3>
                  {scSubtitle && <p className="text-blue-100 text-sm mb-6">{scSubtitle}</p>}
                  {!scSubtitle && <div className="mb-6" />}

                  <div className="space-y-4 text-sm">
                    <div className="flex items-start gap-3">
                      <Clock size={16} className="mt-0.5 shrink-0 opacity-80" />
                      <div>
                        <div className="text-xs uppercase tracking-wider text-blue-200 mb-0.5">{scHoursLabel}</div>
                        <div className="font-medium">{scHoursWeekday}</div>
                        <div className="text-blue-200 text-xs">{scHoursWeekend}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin size={16} className="mt-0.5 shrink-0 opacity-80" />
                      <div className="font-medium leading-relaxed">{scAddress}</div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-2.5">
                    {waNumber && (
                      <a
                        href={`https://wa.me/${waNumber}?text=${encodeURIComponent(scWaMessage)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1EBE57] text-white font-semibold text-sm px-4 py-3 rounded-md transition-colors"
                        data-testid="kontak-whatsapp-cta"
                      >
                        <MessageCircle size={16} /> {scWaLabel}
                      </a>
                    )}
                    {phone && (
                      <a
                        href={`tel:${phone.replace(/\s+/g, '')}`}
                        className="flex items-center justify-center gap-2 w-full bg-white text-[#0a3d8f] hover:bg-white/90 font-semibold text-sm px-4 py-3 rounded-md transition-colors"
                        data-testid="kontak-call-cta"
                      >
                        <Phone size={16} /> {scPhoneLabel}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact form (existing shared section) */}
        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

export default KontakPage;
